import React, { useState } from "react";
import { Box, IconButton, Collapse, Typography } from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import QuestionPanel from "./QuestionPanel";
import EditorSection from "./EditorSection";

/**
 * Mobile Programming Layout Component
 * Optimized layout for mobile devices with collapsible question panel
 */
const MobileProgrammingLayout = ({
  // Question Panel Props
  questionTitle,
  questionContent,
  sampleInput,
  sampleOutput,
  timeLimit,
  memoryLimit,
  maxCodeSize,
  // ✅ REMOVED: maxTime - Programming questions no longer have per-question time limits
  supportedLanguages,
  // Editor Props
  code,
  setCode,
  selectedLanguage,
  setSelectedLanguage,
  editorTheme,
  setEditorTheme,
  isRunningCode,
  isSubmitting,
  attemptsExhausted,
  attemptsUsed,
  maxAttempts,
  onRunCode,
  onSubmit,
  onFormatCode,
  saveState,
  lastSavedAt,
  timeRemaining,
  showOutput,
  setShowOutput,
  isOutputFloating,
  editorRef,
  monacoRef,
  trackEditorEvent,
  lastChangeEventTimeRef,
  lastCursorEventTimeRef,
  lastSelectionEventTimeRef,
  lastFocusEventTimeRef,
  CHANGE_THROTTLE_MS,
  CURSOR_THROTTLE_MS,
  SELECTION_THROTTLE_MS,
  FOCUS_THROTTLE_MS,
  typingAnalyzer,
  componentContainerRef,
  monacoLanguage,
  onLanguageChange,
  // Output Panel Props
  activeTab,
  setActiveTab,
  testCases,
  customInput,
  setCustomInput,
  customResult,
  customLoading,
  onRunCustomInput,
  results,
  summary,
  showCelebration,
  setShowCelebration,
  onFloatingToggle,
  vtLoading,
  outputHeight,
  questionId,
  isFirstProgrammingQuestion = false,
  onModalStateChange,
  isReadOnly,
  isAnswered,
}) => {
  const [isQuestionExpanded, setIsQuestionExpanded] = useState(true);

  const questionContainerStyles = {
    width: "100%",
    px: 1.5,
    py: 1,
    bgcolor: "#fff",
    borderRadius: "0 0 4px 4px", // Rounded corners only at bottom
    position: "relative",
    border: "none", // Border handled by parent container
    flexShrink: 0,
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%", // Use full height of parent container
        width: "100%",
        overflow: "hidden",
        position: "relative",
        gap: 1,
        pb: 1, // Reduced padding to give more space
      }}
    >
      {/* Question Panel with Collapse Toggle */}
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "4px",
          border: "1px solid #e0e0e0",
          overflow: "hidden",
        }}
      >
        {/* Collapse Toggle Button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 1,
            borderBottom: "1px solid #e0e0e0",
            bgcolor: "#f5f5f5",
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 600,
              color: "#333",
            }}
          >
            Question
          </Typography>
          <IconButton
            onClick={() => setIsQuestionExpanded(!isQuestionExpanded)}
            size="small"
            sx={{
              minWidth: "44px",
              minHeight: "44px",
            }}
          >
            {isQuestionExpanded ? (
              <ExpandLess sx={{ fontSize: "24px" }} />
            ) : (
              <ExpandMore sx={{ fontSize: "24px" }} />
            )}
          </IconButton>
        </Box>

        {/* Collapsible Question Content */}
        <Collapse in={isQuestionExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              maxHeight: "40vh",
              overflowY: "auto",
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <QuestionPanel
              questionTitle={questionTitle}
              questionContent={questionContent}
              sampleInput={sampleInput}
              sampleOutput={sampleOutput}
              timeLimit={timeLimit}
              memoryLimit={memoryLimit}
              maxCodeSize={maxCodeSize}
              supportedLanguages={supportedLanguages}
              questionContainerStyles={questionContainerStyles}
              isMobile={true}
            />
          </Box>
        </Collapse>
      </Box>

      {/* Editor Section */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0, // Important for flex children
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <EditorSection
          code={code}
          setCode={setCode}
          selectedLanguage={selectedLanguage}
          setSelectedLanguage={setSelectedLanguage}
          editorTheme={editorTheme}
          setEditorTheme={setEditorTheme}
          isRunningCode={isRunningCode}
          isSubmitting={isSubmitting}
          attemptsExhausted={attemptsExhausted}
          attemptsUsed={attemptsUsed}
          maxAttempts={maxAttempts}
          supportedLanguages={supportedLanguages}
          onRunCode={onRunCode}
          onSubmit={onSubmit}
          onFormatCode={onFormatCode}
          saveState={saveState}
          lastSavedAt={lastSavedAt}
          timeRemaining={timeRemaining}
          isReadOnly={isReadOnly}
          isAnswered={isAnswered}
          showOutput={showOutput}
          setShowOutput={setShowOutput}
          isOutputFloating={false}
          editorRef={editorRef}
          monacoRef={monacoRef}
          trackEditorEvent={trackEditorEvent}
          lastChangeEventTimeRef={lastChangeEventTimeRef}
          lastCursorEventTimeRef={lastCursorEventTimeRef}
          lastSelectionEventTimeRef={lastSelectionEventTimeRef}
          lastFocusEventTimeRef={lastFocusEventTimeRef}
          CHANGE_THROTTLE_MS={CHANGE_THROTTLE_MS}
          CURSOR_THROTTLE_MS={CURSOR_THROTTLE_MS}
          SELECTION_THROTTLE_MS={SELECTION_THROTTLE_MS}
          FOCUS_THROTTLE_MS={FOCUS_THROTTLE_MS}
          typingAnalyzer={typingAnalyzer}
          componentContainerRef={componentContainerRef}
          monacoLanguage={monacoLanguage}
          onLanguageChange={onLanguageChange}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          testCases={testCases}
          customInput={customInput}
          setCustomInput={setCustomInput}
          customResult={customResult}
          customLoading={customLoading}
          onRunCustomInput={onRunCustomInput}
          results={results}
          summary={summary}
          showCelebration={showCelebration}
          setShowCelebration={setShowCelebration}
          onFloatingToggle={onFloatingToggle}
          vtLoading={vtLoading}
          outputHeight={outputHeight}
          isMobile={true}
          questionId={questionId}
          isFirstProgrammingQuestion={isFirstProgrammingQuestion}
          onModalStateChange={onModalStateChange}
        />
      </Box>
    </Box>
  );
};

export default MobileProgrammingLayout;
